// ── Step3 Bindings — Event listeners + state subscriptions ───────
// Wires DOM events and state changes to render/vm functions.

import {
  getState,
  setState,
  subscribe,
} from '../../../state.js';
import {
  normalizeText,
  copyText,
  getErrorMessage,
} from '../../../utils.js';
import {
  translateListingText,
  translateListingQa,
} from '../../../step3/api.js';
import { formatDescriptionPlainText } from '../../../step3/format.js';
import { scheduleStep3AutoSave, hasPendingStep3AutoSave } from '../../../step3/autosave.js';
import { getByteSize } from '../../../byte-counter.js';
import { normalizeTitlePayload } from '../../../interaction/adapters/listing-draft.adapter.js';
import {
  resolveItemHighlights,
  resolveProductTitle,
} from '../../../interaction/builders/listing-draft-normalizers.js';
import {
  $,
  getBatchSnapshotsList,
  getIncludedCompetitorAsins,
  selectCompetitorView,
  isListingReadyForStep4,
} from './vm.js';
import {
  renderCompetitorCard,
  renderCompetitorMultiBar,
  renderGeneratedListing,
  renderExport,
  updateChecklist,
  getSearchTermsPreviewFromViewModel,
} from './render.js';
import { buildListingResultViewModel } from '../../../view-models/listing-result.viewmodel.js';
import { openOptimizationCenterForDiagnosis } from '../../../step4-compliance.js';
import { showToast } from '../../../toast.js';

// ── Gather all data needed for render ────────────────────────────

function gatherViewModel() {
  return buildListingResultViewModel({
    title: getState('listingDraftTitle'),
    bullets: getState('listingDraftBullets'),
    description: getState('listingDraftDescription'),
    searchTerms: getState('listingDraftSearchTerms'),
    qa: getState('listingDraftQa'),
    selectedTitleVersion: getState('selectedTitleVersion'),
    listingDraftV1: getState('listingDraftV1'),
    generationMeta: getState('listingGenerationMeta'),
    sectionErrors: getState('listingGenerationSectionErrors'),
  });
}

function gatherRenderContext() {
  return {
    titleState: getState('listingDraftTitle'),
    bulletsState: getState('listingDraftBullets'),
    descriptionState: getState('listingDraftDescription'),
    stState: getState('listingDraftSearchTerms'),
    qaState: getState('listingDraftQa'),
    optimizerResult: getState('optimizerResult'),
    generationMeta: getState('listingGenerationMeta'),
    sectionErrors: getState('listingGenerationSectionErrors'),
    titleShowingZh: getState('titleShowingZh'),
    titleZhMap: getState('titleZhMap'),
    selectedTitleVersion: getState('selectedTitleVersion'),
    listingDraftV1: getState('listingDraftV1'),
    bulletsShowingZh: getState('bulletsShowingZh'),
    bulletsZhMap: getState('bulletsZhMap'),
    descriptionShowingZh: getState('descriptionShowingZh'),
    descriptionZhText: getState('descriptionZhText'),
    aplusShowingZh: getState('aplusShowingZh'),
    aplusZhMap: getState('aplusZhMap'),
    searchTermsShowingZh: getState('searchTermsShowingZh'),
    searchTermsZhText: getState('searchTermsZhText'),
  };
}

function syncExportPreview() {
  const exportText = buildExportText();
  setState('exportText', exportText);
  renderExport(exportText);
}

function setExportTranslationButtonState(button, state = 'default') {
  button.classList.remove('is-original', 'is-loading', 'is-error');
  if (state !== 'default') button.classList.add(`is-${state}`);
}

function renderListingPanels() {
  const vm = gatherViewModel();
  renderGeneratedListing(vm, gatherRenderContext());
  updateChecklist(vm);
  syncExportPreview();
}

function buildExportText() {
  const title = getState('listingDraftTitle');
  const bullets = getState('listingDraftBullets')?.bulletsEn || [];
  const desc = formatDescriptionPlainText(getState('listingDraftDescription')?.descriptionEn || '');
  const st = normalizeText(getState('listingDraftSearchTerms')?.searchTermsEn || '');
  const qa = getState('listingDraftQa') || [];
  const aplusModules = getState('listingDraftDescription')?.aplusModules || [];

  const firstOption = title?.titleOptions?.[0];
  const productTitle = resolveProductTitle(title, firstOption);
  const itemHighlights = resolveItemHighlights(title, firstOption);
  const lines = [
    '# Listing Draft',
    '',
    '## Product Title',
    productTitle || '(未生成)',
    '',
    '## Item Highlights（商品亮点）',
    itemHighlights || '(未生成)',
    '',
  ];

  if (bullets.length) {
    lines.push('## Bullets (五点描述)');
    bullets.forEach((b, i) => lines.push(`${i + 1}. ${b}`));
    lines.push('');
  }
  if (desc) {
    lines.push('## Description (产品描述)', desc, '');
  }
  if (qa.length) {
    lines.push('## Q&A (Alexa 问答)');
    qa.forEach((item) =>
      lines.push(`问：${item.questionEn}`, `答：${item.answerEn}`, '')
    );
  }
  if (aplusModules.length) {
    lines.push('## A+ Modules (A+内容模块)');
    aplusModules.forEach((m, i) => {
      lines.push(`### ${m.moduleType || `模块${i + 1}`}`);
      if (m.headline) lines.push(`**${m.headline}**`);
      if (m.bodyText) lines.push(m.bodyText);
      lines.push('');
    });
  }
  if (st) {
    lines.push('## Search Terms (后台搜索词)', st, '');
  }

  return lines.join('\n');
}

// ── Competitor Render Helper ─────────────────────────────────────

function renderCompetitorCardFull() {
  const snapshot = getState('snapshotState');
  const list = getBatchSnapshotsList();
  const included = getIncludedCompetitorAsins();
  window.__step3SelectedCompetitorIndex = getState('selectedCompetitorIndex') || 0;
  renderCompetitorCard(snapshot, list, included);
}

// ── Title Version Click Handler ──────────────────────────────────

function handleTitleVersionClick(e) {
  if (window.getSelection && window.getSelection().toString().trim()) {
    return;
  }

  const card = e.target.closest('.title-version-card');
  if (!card || card.dataset.index === undefined) return;

  const idx = parseInt(card.dataset.index, 10);
  setState('selectedTitleVersion', idx);

  const title = getState('listingDraftTitle');
  if (title?.titleOptions?.[idx]) {
    const option = title.titleOptions[idx];
    const productTitle = resolveProductTitle(option, title);
    const itemHighlights = resolveItemHighlights(option, title);
    setState('listingDraftTitle', normalizeTitlePayload({
      ...title,
      productTitle,
      itemHighlights,
      titleEn: productTitle,
      titleMain: productTitle,
      titleSub: itemHighlights,
    }));
  }
}

let qaTranslationInFlight = false;

function qaRowsFromState(value) {
  const rows = Array.isArray(value) ? value : (Array.isArray(value?.qa) ? value.qa : []);
  return rows.filter((item) => item && typeof item === 'object');
}

function replaceQaRows(value, rows) {
  return Array.isArray(value) ? rows : { ...(value || {}), qa: rows };
}

function hasSameQaSource(currentRows, sourceRows) {
  return currentRows.length === sourceRows.length && currentRows.every((item, index) => (
    String(item?.questionEn || '').trim() === sourceRows[index].questionEn
    && String(item?.answerEn || '').trim() === sourceRows[index].answerEn
  ));
}

function qaTranslationFailureMessage(error) {
  const detail = getErrorMessage(error);
  // Keep the provider detail for support diagnostics, but do not expose it as
  // an operator-facing error. Translation is optional display assistance; the
  // English QA must stay available and retryable if its provider is unavailable.
  if (/insufficient[_\s-]?balance|余额不足|\b402\b/iu.test(detail)) {
    return 'QA 中文翻译服务暂时不可用，英文 QA 已保留，可稍后重试。';
  }
  return 'QA 中文翻译暂时未完成，英文 QA 已保留，可稍后重试。';
}

// ── Event Bindings ───────────────────────────────────────────────

export function bindEvents({ generateAllFromBatch, retrySearchTeamFromTask }) {
  // 防止延迟保存尚未落库时直接离页丢失数据。
  window.addEventListener('beforeunload', (event) => {
    if (!hasPendingStep3AutoSave()) return;
    event.preventDefault();
    event.returnValue = '';
  });

  // Expose refresh hook for Step06
  if (typeof window !== 'undefined') {
    window.__refreshStep3ListingView = () => {
      renderListingPanels();
    };
  }
  // Review strip diagnostic button
  $('#step3-review-strip')?.addEventListener('click', (e) => {
    const btn = e.target.closest('#step3-open-diagnostic-button');
    if (!btn || btn.disabled) return;
    openOptimizationCenterForDiagnosis();
  });

  // Navigation
  $('#step3-prev-button')?.addEventListener('click', () => setState('currentStep', 2));
  $('#step3-next-button')?.addEventListener('click', () => {
    if (!isListingReadyForStep4()) {
      showToast('请先生成完整的 Listing（至少包含标题与五点）再进入下一步', { id: 'step3-listing-toast', type: 'error' });
      return;
    }
    openOptimizationCenterForDiagnosis();
  });

  // Step 3 Tab bar switching
  const step3TabsNav = $('#step3-tabs-nav');
  if (step3TabsNav) {
    step3TabsNav.addEventListener('click', (e) => {
      const btn = e.target.closest('.tab-button');
      if (!btn) return;
      const tabName = btn.dataset.tab;
      if (!tabName) return;

      // Update active state on tab buttons
      step3TabsNav.querySelectorAll('.tab-button').forEach((b) => {
        const isActive = b === btn;
        b.classList.toggle('active', isActive);
        b.setAttribute('aria-selected', String(isActive));
        b.tabIndex = isActive ? 0 : -1;
      });

      // Update active state on tab panes
      const container = step3TabsNav.nextElementSibling;
      if (container) {
        container.querySelectorAll('.tab-pane').forEach((pane) => {
          const isTarget = pane.id === `pane-step3-${tabName}`;
          pane.classList.toggle('active', isTarget);
          pane.hidden = !isTarget;
        });
      }
    });

    step3TabsNav.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const tabs = Array.from(step3TabsNav.querySelectorAll('.tab-button'));
      const currentIndex = tabs.indexOf(document.activeElement);
      if (currentIndex < 0) return;
      event.preventDefault();
      const nextIndex = event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? tabs.length - 1
          : (currentIndex + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      tabs[nextIndex].focus();
      tabs[nextIndex].click();
    });
  }

  // Generate button
  $('#generate-all-button')?.addEventListener('click', generateAllFromBatch);
  $('#step3-search-terms-container')?.addEventListener('click', (event) => {
    const button = event.target.closest('#retry-search-team-button');
    if (!button || button.disabled) return;
    retrySearchTeamFromTask?.();
  });

  // Q&A is written first by the dedicated QA/A+ Agent. Chinese is requested
  // only afterwards from a translation-only endpoint so it cannot alter facts
  // or make the actual Listing generation fail.
  $('#step3-qa-container')?.addEventListener('click', async (event) => {
    const button = event.target.closest('#translate-qa-button');
    if (!button || button.disabled || qaTranslationInFlight) return;

    const originalQa = getState('listingDraftQa');
    const sourceRows = qaRowsFromState(originalQa).map((item) => ({
      questionEn: String(item.questionEn || '').trim(),
      answerEn: String(item.answerEn || '').trim(),
    })).filter((item) => item.questionEn && item.answerEn);
    if (!sourceRows.length) return;

    const jobId = String(getState('batchJobId') || '');
    qaTranslationInFlight = true;
    button.disabled = true;
    button.textContent = '翻译 QA 中…';
    try {
      const result = await translateListingQa(sourceRows);
      const translations = Array.isArray(result?.translations) ? result.translations : [];
      if (translations.length !== sourceRows.length) throw new Error('QA 翻译结果不完整');

      // An old asynchronous response must never overwrite a new product task.
      if (String(getState('batchJobId') || '') !== jobId) return;
      const latestQa = getState('listingDraftQa');
      const latestRows = qaRowsFromState(latestQa);
      if (!hasSameQaSource(latestRows, sourceRows)) return;

      const translatedRows = latestRows.map((item, index) => ({
        ...item,
        questionZh: String(translations[index]?.questionZh || '').trim(),
        answerZh: String(translations[index]?.answerZh || '').trim(),
      }));
      if (translatedRows.some((item) => !item.questionZh || !item.answerZh)) {
        throw new Error('QA 翻译结果不完整');
      }
      setState('listingDraftQa', replaceQaRows(latestQa, translatedRows));
      scheduleStep3AutoSave(jobId);
      renderListingPanels();
      showToast('QA 中文对照已独立生成并保存', { id: 'step3-qa-translation', type: 'success' });
    } catch (err) {
      console.error('QA 翻译失败:', err);
      button.title = getErrorMessage(err);
      showToast(qaTranslationFailureMessage(err), {
        id: 'step3-qa-translation',
        type: 'error',
      });
    } finally {
      qaTranslationInFlight = false;
      if (button.isConnected) {
        button.disabled = false;
        button.textContent = '翻译 QA';
      }
    }
  });

  // Copy buttons
  $('#copy-search-terms')?.addEventListener('click', () => {
    copyText(getState('listingDraftSearchTerms')?.searchTermsEn || '');
  });
  $('#copy-export')?.addEventListener('click', () => {
    copyText(getState('exportText') || '');
  });

  // Translate export
  $('#translate-export')?.addEventListener('click', async () => {
    const exportText = getState('exportText') || '';
    if (!exportText) return;

    const btn = $('#translate-export');

    if (getState('exportShowingZh')) {
      setState('exportShowingZh', false);
      renderExport(exportText);
      btn.textContent = '中文翻译';
      setExportTranslationButtonState(btn);
      return;
    }

    const cachedZh = getState('exportTextZh');
    if (cachedZh) {
      setState('exportShowingZh', true);
      renderExport(cachedZh);
      btn.textContent = '查看原文';
      setExportTranslationButtonState(btn, 'original');
      return;
    }

    btn.disabled = true;
    btn.textContent = '翻译中...';
    setExportTranslationButtonState(btn, 'loading');

    try {
      const data = await translateListingText(exportText);
      const zh = String(data.translatedText || '').trim();
      if (!zh) throw new Error('翻译结果为空');
      setState('exportTextZh', zh);
      setState('exportShowingZh', true);
      renderExport(zh);
      btn.textContent = '查看原文';
      setExportTranslationButtonState(btn, 'original');
      btn.title = '';
    } catch (err) {
      console.error('翻译失败:', err);
      btn.textContent = '翻译失败';
      setExportTranslationButtonState(btn, 'error');
      btn.title = getErrorMessage(err);
      setTimeout(() => {
        btn.textContent = '中文翻译';
        setExportTranslationButtonState(btn);
        btn.title = '';
      }, 2000);
    } finally {
      btn.disabled = false;
    }
  });

  // Translate title
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('#translate-title');
    if (!btn) return;

    if (getState('titleShowingZh')) {
      setState('titleShowingZh', false);
      renderListingPanels();
      return;
    }

    const cached = getState('titleZhMap');
    if (cached && Object.keys(cached).length > 0) {
      setState('titleShowingZh', true);
      renderListingPanels();
      return;
    }

    const title = getState('listingDraftTitle');
    if (!title) return;
    const options = Array.isArray(title.titleOptions) ? title.titleOptions : [];
    const texts = options.length > 0
      ? options.map((opt) => resolveProductTitle(opt, title))
      : [resolveProductTitle(title)];
    if (texts.every((t) => !t)) return;

    // 图一：同时翻译独立的 Item Highlights 文本。
    const highlightTexts = options.length > 0
      ? options.map((opt) => resolveItemHighlights(opt, title))
      : [resolveItemHighlights(title)];

    btn.disabled = true;
    btn.textContent = '翻译中...';

    try {
      // 逐项翻译，服务端不需要也不允许依赖保留人为分隔符。
      const translateTexts = (values) => Promise.all(values.map((value) => (
        value ? translateListingText(value) : Promise.resolve(null)
      )));
      const [titleTranslations, highlightTranslations] = await Promise.all([
        translateTexts(texts),
        translateTexts(highlightTexts),
      ]);
      const zhMap = {};
      texts.forEach((_, i) => { zhMap[i] = String(titleTranslations[i]?.translatedText || '').trim(); });
      highlightTexts.forEach((_, i) => { zhMap[`hl_${i}`] = String(highlightTranslations[i]?.translatedText || '').trim(); });
      if (!Object.values(zhMap).some(Boolean)) {
        throw new Error('翻译结果为空');
      }
      setState('titleZhMap', zhMap);
      setState('titleShowingZh', true);
      renderListingPanels();
    } catch (err) {
      console.error('标题翻译失败:', err);
      btn.textContent = '翻译失败';
      btn.title = getErrorMessage(err);
      setTimeout(() => { btn.textContent = '中文理解'; btn.disabled = false; btn.title = ''; }, 2000);
    }
  });

  // Translate bullets（五点逐条并行翻译，参考 title 模式）
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('#translate-bullets');
    if (!btn) return;

    if (getState('bulletsShowingZh')) {
      setState('bulletsShowingZh', false);
      renderListingPanels();
      return;
    }

    const cached = getState('bulletsZhMap');
    if (cached && Object.keys(cached).length > 0) {
      setState('bulletsShowingZh', true);
      renderListingPanels();
      return;
    }

    const bullets = getState('listingDraftBullets');
    const rawItems = bullets?.bulletsEn;
    const items = Array.isArray(rawItems) ? rawItems : (rawItems ? [rawItems] : []);
    const texts = items.map((item) => typeof item === 'string' ? item : (item?.text || item?.lead || ''));
    if (texts.every((t) => !t)) return;

    btn.disabled = true;
    btn.textContent = '翻译中...';

    try {
      const translations = await Promise.all(texts.map((value) => (
        value ? translateListingText(value) : Promise.resolve(null)
      )));
      const zhMap = {};
      texts.forEach((_, i) => { zhMap[i] = String(translations[i]?.translatedText || '').trim(); });
      if (!Object.values(zhMap).some(Boolean)) throw new Error('翻译结果为空');
      setState('bulletsZhMap', zhMap);
      setState('bulletsShowingZh', true);
      renderListingPanels();
    } catch (err) {
      console.error('五点翻译失败:', err);
      btn.textContent = '翻译失败';
      btn.title = getErrorMessage(err);
      setTimeout(() => { btn.textContent = '中文理解'; btn.disabled = false; btn.title = ''; }, 2000);
    }
  });

  // Translate description（整段翻译）
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('#translate-description');
    if (!btn) return;

    if (getState('descriptionShowingZh')) {
      setState('descriptionShowingZh', false);
      renderListingPanels();
      return;
    }

    const cachedZh = getState('descriptionZhText');
    if (cachedZh) {
      setState('descriptionShowingZh', true);
      renderListingPanels();
      return;
    }

    const desc = getState('listingDraftDescription');
    const text = typeof desc?.descriptionEn === 'string' ? desc.descriptionEn : '';
    if (!text) return;

    btn.disabled = true;
    btn.textContent = '翻译中...';

    try {
      const data = await translateListingText(text);
      const zh = String(data?.translatedText || '').trim();
      if (!zh) throw new Error('翻译结果为空');
      setState('descriptionZhText', zh);
      setState('descriptionShowingZh', true);
      renderListingPanels();
    } catch (err) {
      console.error('描述翻译失败:', err);
      btn.textContent = '翻译失败';
      btn.title = getErrorMessage(err);
      setTimeout(() => { btn.textContent = '中文理解'; btn.disabled = false; btn.title = ''; }, 2000);
    }
  });

  // Translate search terms（后台搜索词翻译）
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('#translate-searchterms');
    if (!btn) return;

    if (getState('searchTermsShowingZh')) {
      setState('searchTermsShowingZh', false);
      renderListingPanels();
      return;
    }

    const cachedZh = getState('searchTermsZhText');
    if (cachedZh) {
      setState('searchTermsShowingZh', true);
      renderListingPanels();
      return;
    }

    const st = getState('listingDraftSearchTerms');
    const text = st?.searchTermsEn || '';
    if (!text) return;

    btn.disabled = true;
    btn.textContent = '翻译中...';

    try {
      const data = await translateListingText(text);
      const zh = String(data?.translatedText || '').trim();
      if (!zh) throw new Error('翻译结果为空');
      setState('searchTermsZhText', zh);
      setState('searchTermsShowingZh', true);
      renderListingPanels();
    } catch (err) {
      console.error('搜索词翻译失败:', err);
      btn.textContent = '翻译失败';
      btn.title = getErrorMessage(err);
      setTimeout(() => { btn.textContent = '中文理解'; btn.disabled = false; btn.title = ''; }, 2000);
    }
  });

  // Translate A+ modules（逐模块并行翻译）
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('#translate-aplus');
    if (!btn) return;

    if (getState('aplusShowingZh')) {
      setState('aplusShowingZh', false);
      renderListingPanels();
      return;
    }

    const cached = getState('aplusZhMap');
    if (cached && Object.keys(cached).length > 0) {
      setState('aplusShowingZh', true);
      renderListingPanels();
      return;
    }

    const desc = getState('listingDraftDescription');
    const modules = Array.isArray(desc?.aplusModules) ? desc.aplusModules : [];
    const texts = modules.map((m) => [m?.headline, m?.bodyText].filter(Boolean).join('\n'));
    if (texts.every((t) => !t)) return;

    btn.disabled = true;
    btn.textContent = '翻译中...';

    try {
      const translations = await Promise.all(texts.map((value) => (
        value ? translateListingText(value) : Promise.resolve(null)
      )));
      const zhMap = {};
      texts.forEach((_, i) => { zhMap[i] = String(translations[i]?.translatedText || '').trim(); });
      if (!Object.values(zhMap).some(Boolean)) throw new Error('翻译结果为空');
      setState('aplusZhMap', zhMap);
      setState('aplusShowingZh', true);
      renderListingPanels();
    } catch (err) {
      console.error('A+翻译失败:', err);
      btn.textContent = '翻译失败';
      btn.title = getErrorMessage(err);
      setTimeout(() => { btn.textContent = '中文理解'; btn.disabled = false; btn.title = ''; }, 2000);
    }
  });

  // Individual copy buttons
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-copy-title], [data-copy-highlight], [data-copy-bullet], [data-copy-description], [data-copy-aplus], [data-copy-searchterms], [data-copy-qa-english-pair], [data-copy-qa-module]');
    if (!btn) return;

    e.stopPropagation();

    if (btn.dataset.copyTitle !== undefined) {
      const idx = parseInt(btn.dataset.copyTitle, 10);
      const title = getState('listingDraftTitle');
      let text = '';
      if (title?.titleOptions?.[idx]) {
        text = resolveProductTitle(title.titleOptions[idx], title);
      } else if (idx === 0) {
        text = resolveProductTitle(title);
      }
      if (text) {
        copyText(text);
        btn.textContent = '✅';
        setTimeout(() => { btn.textContent = '📋'; }, 1500);
      }
    }

    if (btn.dataset.copyHighlight !== undefined) {
      const idx = parseInt(btn.dataset.copyHighlight, 10);
      const title = getState('listingDraftTitle');
      let text = '';
      if (title?.titleOptions?.[idx]) {
        text = resolveItemHighlights(title.titleOptions[idx], title);
      } else if (idx === 0) {
        text = resolveItemHighlights(title);
      }
      if (text) {
        copyText(text);
        btn.textContent = '✅';
        setTimeout(() => { btn.textContent = '📋'; }, 1500);
      }
    }

    if (btn.dataset.copyBullet !== undefined) {
      const idx = parseInt(btn.dataset.copyBullet, 10);
      const bullets = getState('listingDraftBullets')?.bulletsEn || [];
      const text = bullets[idx] || '';
      if (text) {
        copyText(text);
        btn.textContent = '✅';
        setTimeout(() => { btn.textContent = '📋'; }, 1500);
      }
    }

    if (btn.dataset.copyDescription !== undefined) {
      const desc = formatDescriptionPlainText(getState('listingDraftDescription')?.descriptionEn || '');
      if (desc) {
        copyText(desc);
        btn.textContent = '✅';
        setTimeout(() => { btn.textContent = '📋'; }, 1500);
      }
    }

    if (btn.dataset.copyAplus !== undefined) {
      const idx = parseInt(btn.dataset.copyAplus, 10);
      const modules = getState('listingDraftDescription')?.aplusModules || [];
      const mod = modules[idx];
      if (mod) {
        const parts = [];
        if (mod.moduleType) parts.push(`[${mod.moduleType}]`);
        if (mod.headline) parts.push(mod.headline);
        if (mod.bodyText) parts.push(mod.bodyText);
        const text = parts.join('\n\n');
        if (text) {
          copyText(text);
          btn.textContent = '✅';
          setTimeout(() => { btn.textContent = '📋'; }, 1500);
        }
      }
    }

    if (btn.dataset.copySearchterms !== undefined) {
      const st = getState('listingDraftSearchTerms')?.searchTermsEn || '';
      if (st) {
        copyText(st);
        btn.textContent = '✅';
        setTimeout(() => { btn.textContent = '📋'; }, 1500);
      }
    }

    if (btn.dataset.copyQaEnglishPair !== undefined) {
      const idx = parseInt(btn.dataset.copyQaEnglishPair, 10);
      const qa = getState('listingDraftQa') || [];
      const item = qa[idx];
      if (item) {
        const text = [`Q: ${item.questionEn}`, `A: ${item.answerEn}`].join('\n');
        copyText(text);
        btn.textContent = '✅ 已复制';
        setTimeout(() => { btn.textContent = '📋 复制'; }, 1500);
      }
    }

    if (btn.dataset.copyQaModule !== undefined) {
      const qa = getState('listingDraftQa') || [];
      if (qa.length) {
        const text = qa.map((item, idx) => `Q${idx + 1}: ${item.questionEn}\nA${idx + 1}: ${item.answerEn}`).join('\n\n');
        copyText(text);
        const originalText = btn.textContent;
        btn.textContent = '✅ 已复制全部 QA';
        setTimeout(() => { btn.textContent = originalText; }, 1500);
      }
    }
  });

  // V1 comparison toggle
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-v1-toggle]');
    if (!toggle) return;
    const content = toggle.querySelector('.v1-compare-content');
    if (!content) return;
    const isOpen = !content.hidden;
    content.hidden = isOpen;
    const label = toggle.querySelector('.v1-toggle-label');
    if (label) {
      label.textContent = isOpen ? '▶ 原始版本 (V1)' : '▼ 原始版本 (V1)';
    }
  });

  // Title version click
  document.addEventListener('click', handleTitleVersionClick);

  // Competitor tabs
  document.addEventListener('click', (e) => {
    const tab = e.target.closest('[data-competitor-index]');
    if (tab && tab.classList.contains('competitor-tab')) {
      const index = parseInt(tab.dataset.competitorIndex, 10);
      if (!Number.isNaN(index)) selectCompetitorView(index);
      return;
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const tab = event.target.closest('.competitor-tab');
    const tablist = tab?.closest('#competitor-asin-tabs');
    if (!tablist) return;

    const tabs = Array.from(tablist.querySelectorAll('.competitor-tab'));
    const currentIndex = tabs.indexOf(tab);
    if (currentIndex < 0) return;
    event.preventDefault();

    const nextIndex = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? tabs.length - 1
        : (currentIndex + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    const nextTab = tabs[nextIndex];
    const competitorIndex = Number.parseInt(nextTab.dataset.competitorIndex, 10);
    if (Number.isNaN(competitorIndex)) return;
    selectCompetitorView(competitorIndex);
    requestAnimationFrame(() => {
      document.getElementById(`competitor-tab-${competitorIndex}`)?.focus();
    });
  });

  // Competitor include checkboxes
  document.addEventListener('change', (e) => {
    const cb = e.target.closest('input[data-competitor-asin]');
    if (!cb) return;
    const asin = cb.dataset.competitorAsin;
    if (!asin) return;
    const list = getBatchSnapshotsList();
    const included = new Set(getIncludedCompetitorAsins());
    if (cb.checked) {
      included.add(asin);
    } else {
      included.delete(asin);
      if (included.size === 0) {
        cb.checked = true;
        included.add(asin);
        return;
      }
    }
    setState('competitorAsinsIncluded', included);
    const hint = $('#competitor-merge-hint');
    if (hint && list.length > 1) {
      hint.textContent =
        `生成 Listing 将综合已勾选的 ${included.size}/${list.length} 个竞品 ASIN：对比各竞品标题/五点/描述后，生成我方产品文案（不会照搬任一竞品品牌）。`;
    }
  });
}

// ── State Subscriptions ──────────────────────────────────────────

export function bindStateSubscriptions() {
  // Next button state
  function updateNextButton() {
    const btn = $('#step3-next-button');
    if (!btn) return;
    btn.disabled = !isListingReadyForStep4();
  }

  subscribe('state:listingDraftTitleChanged', updateNextButton);
  subscribe('state:listingDraftBulletsChanged', updateNextButton);
  subscribe('state:listingGenerationMetaChanged', updateNextButton);
  updateNextButton();

  // Competitor card
  subscribe('state:snapshotStateChanged', () => {
    renderCompetitorCardFull();
  });

  // Listing re-render on any draft change
  subscribe('state:listingDraftTitleChanged', () => {
    renderListingPanels();
  });

  subscribe('state:listingDraftBulletsChanged', () => {
    renderListingPanels();
  });

  subscribe('state:listingDraftDescriptionChanged', () => {
    renderListingPanels();
  });

  subscribe('state:listingDraftSearchTermsChanged', () => {
    renderListingPanels();
  });

  subscribe('state:listingDraftQaChanged', () => {
    renderListingPanels();
  });

  subscribe('state:selectedTitleVersionChanged', () => {
    renderListingPanels();
  });

  subscribe('state:listingDraftV1Changed', () => {
    renderListingPanels();
  });

  subscribe('state:optimizerResultChanged', () => {
    renderListingPanels();
  });

  subscribe('state:listingGenerationMetaChanged', () => {
    renderListingPanels();
  });

  subscribe('state:listingGenerationSectionErrorsChanged', () => {
    renderListingPanels();
  });

  // Step activation
  subscribe('state:currentStepChanged', ({ curr }) => {
    if (curr === 3) {
      renderCompetitorCardFull();
      renderListingPanels();

      const title = getState('listingDraftTitle');
      const jobId = getState('batchJobId');
      if (title && (resolveProductTitle(title) || title.titleOptions?.length) && jobId) {
        scheduleStep3AutoSave(jobId);
      }
    }
  });

  // Initial render if already on step 3
  if (getState('currentStep') === 3) {
    renderCompetitorCardFull();
    renderListingPanels();
  }
}
